import {
  AI,
  BOOST_TRAIL,
  DECAY,
  DIGEST,
  PLAYER,
  POWERUP,
  SNAKE,
  WORLD,
  massToRadius,
  radiusToMass,
} from '../constants';
import { angleDelta, clamp, damp, TAU, type Vec2 } from '../utils/math';

/**
 * Monochromatic skin with optional dark-band pattern.
 *
 * Strict identity rule: a snake is ONE hue. The "bioluminescent current" is
 * produced by a brightness wave travelling along the body, NOT by hue shift.
 *
 * Dark-band layer: an OPTIONAL second wave that darkens narrow bands along
 * the body — a "tiger stripe" overlay. Some snakes have it, some don't,
 * and even among those that do the band frequency / speed / depth varies.
 * Combined with the wave-param variation, every snake gets a unique
 * visual signature even when two share a team hue.
 */
/**
 * AI personality archetype — biases bot decisions in SnakeAI.
 *
 *   aggressive: engages bigger snakes, chases more eagerly, ignores fear
 *   cautious:   flees readily, prefers food, low-risk play
 *   hunter:     ignores food entirely, pure prey focus
 *   normal:     balanced default
 */
export type Personality = 'aggressive' | 'cautious' | 'hunter' | 'normal';

/**
 * Visual pattern overlay on top of the monochromatic body.
 *
 *   glitch    — pixelated multi-hue patches that "tick" in time (Glitch Skin)
 *   plasma    — flowing electric arcs along the body in cyan + magenta
 *   geometric — wireframe triangle mesh stretched over the body
 *   bio       — bright cell-membrane cracks dividing the body into "cells"
 *
 * `undefined` keeps the legacy monochromatic look — used by fillers + early
 * versions of rivals before they were assigned signature patterns.
 */
export type SkinPattern = 'glitch' | 'plasma' | 'geometric' | 'bio';

export interface SnakeSkin {
  /** Identity hue in degrees (0-360). */
  hue: number;
  /** Saturation 0-100 (always near 100 for the neon aesthetic). */
  sat: number;
  /** Mid-line lightness 0-100. The wave oscillates around this. */
  baseLight: number;
  /** Brightness wave amplitude (lightness percent, peak-to-mid). */
  lightAmp: number;
  /** Brightness waves per body length. */
  waveFreq: number;
  /** Wave travel speed (cycles per second). */
  waveSpeed: number;
  /** Static offset of the wave phase — desyncs snakes from each other. */
  wavePhase: number;
  /**
   * Dark-band depth, 0..1. 0 = no bands, 1 = bands go fully black at peak.
   * The band intensity multiplies lightness on top of the brightness wave.
   */
  darkBandDepth: number;
  /** Dark-band frequency (bands per body length). */
  darkBandFreq: number;
  /** Dark-band travel speed (cycles per second). 0 = static bands. */
  darkBandSpeed: number;
  /** Dark-band phase offset (radians). */
  darkBandPhase: number;

  /**
   * Plasma orbs — number of bright nodes that flow along the body. 0 = no
   * orbs. Orbs travel head→tail (or vice-versa) over time, giving the snake
   * a clear identity feature ("the one with the orbs"). Drawn as
   * brightest-of-the-body discs on top of the spline.
   */
  orbCount: number;
  /** Orb travel speed along body length (cycles per second). Sign controls direction. */
  orbSpeed: number;

  /**
   * Optional signature pattern overlay drawn on top of the body. When set,
   * the renderer dispatches to a pattern-specific overlay after the base
   * body pass — gives the snake a distinct silhouette beyond just its hue.
   * Omit for the plain monochromatic look (still the default for fillers).
   */
  pattern?: SkinPattern;
}

/**
 * Snake — the single body archetype, used for both player and AI bots.
 *
 * Steering model (Step 3.5):
 *   - The head turns toward the desired heading using **exponential angle
 *     damping**: each frame closes `1 - exp(-stiffness * dt)` of the remaining
 *     angular distance, with wrap-aware delta. This replaces the old hard
 *     "rad/s cap", which produced bang-bang constant-velocity turns. Now turns
 *     ease in and out — the head accelerates into a curve, then settles.
 *   - Stiffness scales inversely with radius: small snakes feel sharp, giants
 *     drift heavily but smoothly.
 *   - The steer target itself is also lightly damped (`STEER_SMOOTHING`) so
 *     pixel-level mouse jitter doesn't reach the heading math at all.
 */
export class Snake {
  public readonly id: number;
  public readonly isPlayer: boolean;
  public name: string;
  public readonly skin: SnakeSkin;
  /**
   * Rival roster slot index. Set on rival bots so the World can detect which
   * profile died and respawn it with the same identity. undefined for player
   * and any one-off snakes.
   */
  public readonly profileIdx: number | undefined;

  /** Behavior archetype — biases AI decisions. */
  public readonly personality: Personality;

  public heading: number;
  /** Raw input target (mouseWorld, AI brain output). */
  public steerTargetRaw: Vec2 = { x: 0, y: 0 };
  /** Smoothed steer target — the head actually heads here. */
  public steerTarget: Vec2 = { x: 0, y: 0 };

  public segments: Vec2[];
  private targetSegCount: number;

  public mass: number;
  public radius: number;

  public alive = true;
  public boosting = false;

  /**
   * Smoothed actual speed (world u/s). Each frame this damps toward the
   * size-and-boost target speed — gives the snake "weight" on accel/decel
   * without input lag. Exposed for the camera lookahead.
   */
  public currentSpeed = 0;

  /** 0..1 attention spike on color-match eat; decays exponentially. */
  public pulse = 0;

  /**
   * 0..1 mouth-open value — driven by recent eats.
   * 1 = mouth wide open (just bit a pellet), 0 = closed.
   * Decays exponentially so each chomp produces a short open-close animation.
   */
  public chomp = 0;

  /** Tongue flick progress, 0..1. Briefly nonzero during a flick animation. */
  public tongue = 0;
  /** Sim-clock time of next scheduled tongue flick. */
  public nextFlickAt = 0;

  /**
   * Sim-clock time until which this snake is invincible. Used for respawn
   * shield + spawn-in grace. Collision logic skips invincible snakes; the
   * renderer flickers their alpha so the player sees they're protected.
   */
  public invincibleUntil = 0;

  /** Sim-clock time at spawn — drives a brief fade-in alpha. */
  public spawnTime = 0;

  /**
   * Digesting orbs — pellets the snake has recently eaten, visible passing
   * through the body from head to tail. Each orb stores its position along
   * the body in `u ∈ [0, 1]` (0 = head, 1 = tail tip) and the hue of the
   * pellet that produced it.
   *
   * Spawned by World on perfect-match eats; ticked here in update().
   */
  public digestingOrbs: Array<{ u: number; hue: number }> = [];

  // ── Powerup state ────────────────────────────────────────────────────────
  /** Seconds of free-cost boost remaining (from Surge powerup). */
  public surgeRemaining = 0;
  /** Seconds of 2× pellet mass remaining (from Double powerup). */
  public doubleMassRemaining = 0;
  /** True if the snake currently holds an unused Aegis shield. */
  public shielded = false;

  /**
   * Accumulator for mass burned during boost. World reads + drains this
   * each tick to spawn trail pellets behind the tail. The actual mass is
   * already subtracted from `mass`; this only tracks "how much hasn't
   * become a pellet yet".
   */
  public boostMassBurned = 0;
  /**
   * Accumulator for backward boost particles (visual only). Counts seconds
   * of boost — World emits one particle per (1 / BOOST_TRAIL.PARTICLE_RATE).
   */
  public boostParticleAccum = 0;

  /**
   * Bioluminescent shader clock. Integrates dt × wave-speed multiplier so
   * the brightness wave can SPEED UP during boost without the renderer
   * having to teleport `time` between frames. Renderer uses
   * `skin.waveSpeed * shaderClock * 2π` for its phase.
   */
  public shaderClock = 0;

  /** True only for the current leaderboard #1 (set by World). Renderer draws the alpha aura. */
  public isLeader = false;

  /** Gold-border cosmetic — set on the PLAYER snake by GameCanvas when
   *  the store's `goldBorderActive` flag flips true (60s reward). The
   *  renderer draws a thin amber outline along the body. Survives
   *  respawn (the store flag is session-scoped). */
  public goldBorder = false;

  /** Wall-clock seconds since spawn — drives wobble + skin animation. */
  public age = 0;

  /** Identity hue — proxy for skin.hue, kept as a getter for ergonomic call sites. */
  public get teamHue(): number {
    return this.skin.hue;
  }

  constructor(opts: {
    id: number;
    isPlayer: boolean;
    name: string;
    skin: SnakeSkin;
    spawn: Vec2;
    heading: number;
    mass: number;
    profileIdx?: number;
    personality?: Personality;
  }) {
    this.id = opts.id;
    this.isPlayer = opts.isPlayer;
    this.name = opts.name;
    this.skin = opts.skin;
    this.profileIdx = opts.profileIdx;
    this.personality = opts.personality ?? 'normal';
    this.heading = opts.heading;
    this.mass = opts.mass;
    this.radius = massToRadius(this.mass);

    this.targetSegCount = this.computeSegCount();
    this.segments = new Array(this.targetSegCount);
    const spacing = this.segmentSpacing();
    const dx = -Math.cos(this.heading);
    const dy = -Math.sin(this.heading);
    for (let i = 0; i < this.targetSegCount; i++) {
      this.segments[i] = {
        x: opts.spawn.x + dx * spacing * i,
        y: opts.spawn.y + dy * spacing * i,
      };
    }
    const aheadX = opts.spawn.x + Math.cos(this.heading) * 220;
    const aheadY = opts.spawn.y + Math.sin(this.heading) * 220;
    this.steerTargetRaw.x = this.steerTarget.x = aheadX;
    this.steerTargetRaw.y = this.steerTarget.y = aheadY;

    // Start at full cruise speed so the snake doesn't have to spool up at spawn.
    this.currentSpeed = this.speed();
  }

  // ---------------------------------------------------------------------------
  // Derived sizing
  // ---------------------------------------------------------------------------

  segmentSpacing(): number {
    return this.radius * SNAKE.SEGMENT_SPACING_FACTOR;
  }

  speed(): number {
    const ratio = PLAYER.START_RADIUS / Math.max(this.radius, PLAYER.START_RADIUS);
    const mult = Math.max(PLAYER.MIN_SPEED_MULT, Math.pow(ratio, PLAYER.SPEED_FALLOFF));
    return PLAYER.BASE_SPEED * mult;
  }

  widthAt(u: number): number {
    const headBulge = 1 + 0.06 * Math.exp(-u * 12);
    const taper = 1 - (1 - SNAKE.TAIL_TAPER) * Math.pow(u, 1.2);
    return this.radius * headBulge * taper;
  }

  /**
   * Exponential angle-follow stiffness for this size.
   * Small snake → high stiffness (snappy). Giant → low stiffness (drifty).
   */
  turnStiffness(): number {
    const t = clamp(
      (this.radius - PLAYER.START_RADIUS) /
        (PLAYER.TURN_STIFFNESS_R_FULL - PLAYER.START_RADIUS),
      0,
      1,
    );
    return PLAYER.TURN_STIFFNESS_MAX + (PLAYER.TURN_STIFFNESS_MIN - PLAYER.TURN_STIFFNESS_MAX) * t;
  }

  private computeSegCount(): number {
    const t = clamp(this.mass / SNAKE.MASS_FOR_MAX_SEGS, 0, 1);
    return Math.round(SNAKE.MIN_SEGMENTS + (SNAKE.MAX_SEGMENTS - SNAKE.MIN_SEGMENTS) * t);
  }

  // ---------------------------------------------------------------------------
  // Per-frame update
  // ---------------------------------------------------------------------------

  update(dt: number): void {
    if (!this.alive) return;
    this.age += dt;

    // 1) Pulse + chomp + tongue decay.
    if (this.pulse > 0.001) this.pulse *= Math.exp(-SNAKE.PULSE_DECAY * dt);
    else this.pulse = 0;
    if (this.chomp > 0.001) this.chomp *= Math.exp(-9 * dt);
    else this.chomp = 0;
    if (this.tongue > 0.001) this.tongue *= Math.exp(-6 * dt);
    else this.tongue = 0;
    // Powerup timers tick down in sim time.
    if (this.surgeRemaining > 0) this.surgeRemaining = Math.max(0, this.surgeRemaining - dt);
    if (this.doubleMassRemaining > 0) this.doubleMassRemaining = Math.max(0, this.doubleMassRemaining - dt);

    // Digesting orbs progress from head (u=0) to tail (u=1). When an orb
    // reaches the tail it's removed — the eat has been "digested".
    if (this.digestingOrbs.length > 0) {
      const step = DIGEST.ORB_SPEED * dt;
      for (let i = this.digestingOrbs.length - 1; i >= 0; i--) {
        const o = this.digestingOrbs[i]!;
        o.u += step;
        if (o.u >= 1) this.digestingOrbs.splice(i, 1);
      }
    }

    // 2) Mass decay.
    if (this.mass > DECAY.THRESHOLD) {
      this.mass -= (this.mass - DECAY.THRESHOLD) * DECAY.RATE * dt;
    }

    // 2.5) Bot passive growth — keeps AI opponents proportional to the
    //      player's snowball. The player accretes via 2.5× perfect
    //      color matches, combo streaks, AND corpse cashing; without
    //      a counterweight, bots only grow through occasional eats and
    //      the room visibly devolves into "only the player is big".
    //      Passive growth tops every bot up at a steady mass/sec rate,
    //      gated by a respawn grace window (so fresh fillers don't
    //      instantly inflate) and a ceiling (so a forgotten 30-min-old
    //      bot can't become a 50k unkillable). Player snake is excluded.
    if (
      !this.isPlayer &&
      this.age > AI.PASSIVE_GROWTH_GRACE_S &&
      this.mass < AI.PASSIVE_GROWTH_CEILING
    ) {
      this.mass += AI.PASSIVE_GROWTH_PER_SEC * dt;
    }

    // 3) Boost drain. Surge auto-applies a stronger boost for free; regular
    //    boost only fires when the player holds SPACE and has enough mass.
    const isSurging = this.surgeRemaining > 0;
    const wantBoost = isSurging || (this.boosting && this.mass > radiusToMass(PLAYER.START_RADIUS) + 200);
    if (wantBoost && !isSurging) {
      const sizeFactor = Math.max(
        0.4,
        1 - Math.log10(Math.max(1, this.mass / 1500)) * 0.3,
      );
      const drain = SNAKE.BOOST_DRAIN * sizeFactor * dt;
      this.mass -= drain;
      // Feed the trail-pellet accumulator with the burned mass. World
      // consumes from this each tick to spawn pellets behind the tail.
      this.boostMassBurned += drain;
    }
    // Backward-particle accumulator — independent of mass drain so even
    // Surge (free boost) emits ignition particles.
    if (wantBoost) {
      this.boostParticleAccum += dt;
    } else {
      // Reset when boost ends so we don't dump a burst on next boost.
      this.boostParticleAccum = 0;
    }

    // Shader clock — advances faster while boost is active so the
    // bioluminescent wave visibly "spools up" during ignition.
    const waveMult = wantBoost ? BOOST_TRAIL.WAVE_SPEED_MULT : 1;
    this.shaderClock += dt * waveMult;

    // 4) Size update.
    this.radius = massToRadius(this.mass);
    const desiredSegs = this.computeSegCount();
    if (desiredSegs > this.segments.length) {
      const tail = this.segments[this.segments.length - 1]!;
      while (this.segments.length < desiredSegs) {
        this.segments.push({ x: tail.x, y: tail.y });
      }
    } else if (desiredSegs < this.segments.length) {
      this.segments.length = desiredSegs;
    }
    this.targetSegCount = desiredSegs;

    // 5) Light steer-target smoothing — absorbs sub-frame mouse jitter only.
    this.steerTarget.x = damp(this.steerTarget.x, this.steerTargetRaw.x, PLAYER.STEER_SMOOTHING, dt);
    this.steerTarget.y = damp(this.steerTarget.y, this.steerTargetRaw.y, PLAYER.STEER_SMOOTHING, dt);

    // 6) Heading: exponential angle damping with dead-zone.
    // Dead-zone: when cursor is right on top of the head, atan2 is jitter-prone.
    // We just coast straight in that region — feels exactly like slither.io.
    const head = this.segments[0]!;
    const dx = this.steerTarget.x - head.x;
    const dy = this.steerTarget.y - head.y;
    const distSq = dx * dx + dy * dy;
    const deadzone = Math.max(PLAYER.STEER_DEADZONE_MIN, this.radius * PLAYER.STEER_DEADZONE_FACTOR);
    if (distSq > deadzone * deadzone) {
      const desired = Math.atan2(dy, dx);
      const delta = angleDelta(this.heading, desired);
      const factor = 1 - Math.exp(-this.turnStiffness() * dt);
      this.heading = (this.heading + delta * factor + TAU) % TAU;
    }

    // 7) Speed ramp + move head.
    // Surge gives a stronger multiplier than regular boost.
    const boostMult = isSurging ? POWERUP.SURGE_BOOST_MULT : SNAKE.BOOST_MULT;
    const targetSpeed = this.speed() * (wantBoost ? boostMult : 1);
    this.currentSpeed = damp(this.currentSpeed, targetSpeed, PLAYER.ACCEL_STIFFNESS, dt);
    head.x += Math.cos(this.heading) * this.currentSpeed * dt;
    head.y += Math.sin(this.heading) * this.currentSpeed * dt;

    // Soft world containment.
    const lo = WORLD.EDGE_PADDING + this.radius;
    const hi = WORLD.SIZE - WORLD.EDGE_PADDING - this.radius;
    head.x = clamp(head.x, lo, hi);
    head.y = clamp(head.y, lo, hi);

    // 8) Damped follow-the-leader chain.
    const spacing = this.segmentSpacing();
    const t = 1 - Math.exp(-SNAKE.CHAIN_STIFFNESS * dt);
    for (let i = 1; i < this.segments.length; i++) {
      const prev = this.segments[i - 1]!;
      const cur = this.segments[i]!;
      const ddx = cur.x - prev.x;
      const ddy = cur.y - prev.y;
      const dist = Math.hypot(ddx, ddy);
      if (dist < 1e-6) continue;
      const tx = prev.x + (ddx / dist) * spacing;
      const ty = prev.y + (ddy / dist) * spacing;
      cur.x += (tx - cur.x) * t;
      cur.y += (ty - cur.y) * t;
    }
  }

  // ---------------------------------------------------------------------------
  // Public mutations
  // ---------------------------------------------------------------------------

  addMass(m: number): void {
    if (!this.alive) return;
    this.mass += m;
  }

  triggerPulse(): void {
    this.pulse = 1;
  }

  /** Triggered by World on every eat — opens the mouth briefly. */
  triggerChomp(): void {
    this.chomp = 1;
  }

  /** Trigger a tongue-flick (called periodically by World). */
  triggerFlick(): void {
    this.tongue = 1;
  }

  /**
   * Spawn a digesting orb at the head — called by World on perfect-color
   * eats. If the cap is exceeded, drop the oldest (closest to the tail)
   * so the latest eat always shows up.
   */
  addDigestingOrb(hue: number): void {
    if (this.digestingOrbs.length >= DIGEST.MAX_ORBS) {
      this.digestingOrbs.shift();
    }
    this.digestingOrbs.push({ u: 0, hue });
  }

  /** Is the snake currently in respawn / spawn invincibility? */
  isInvincible(now: number): boolean {
    return now < this.invincibleUntil;
  }

  removeMass(m: number): number {
    const before = this.mass;
    this.mass = Math.max(radiusToMass(PLAYER.START_RADIUS), this.mass - m);
    return before - this.mass;
  }

  kill(): void {
    this.alive = false;
  }

  get head(): Vec2 {
    return this.segments[0]!;
  }
}
